import { useEffect } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { FormField } from '@/components/Form/FormField';
import { FormManagementProps } from '@/type/form.types';
import { Switch } from '@/components/Controls/Switch';
import { setGroupRequiredFields } from '@/utils/required-fields/group.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { VideoUpload } from '@/components/FileUpload/VideoUpload';
import { useUploadCheckPointVideo } from '@/hooks/check-point.hooks';
import Loader from '@/components/Loading/Loader';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { TextInfo } from '@/components/Message/TextInfo';

interface UploadedVideoData {
  data?: {
    path?: string;
  };
}

const ALLOWED_VIDEO_FORMATS = ['mp4'];

export const GroupConfigsManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const displayInspectionVideoTutorialValue =
    watch &&
    watch(
      'displayInspectionVideoTutorial' as Path<TSchema>,
      data?.displayInspectionVideoTutorial ?? false,
    );

  const { mutate: uploadVideo, isPending: isPendingUploadVideo } =
    useUploadCheckPointVideo({
      handleSuccess: (data: UploadedVideoData) => {
        setValue(
          'inspectionTutorialVideo' as Path<TSchema>,
          (data?.data?.path ?? null) as PathValue<TSchema, Path<TSchema>>,
        );
      },
    });

  useEffect(() => {
    if (setValue && data) {
      setGroupRequiredFields({ setValue, data });
      setValue('companyName' as Path<TSchema>, data?.companyName ?? '');
      setValue('branchName' as Path<TSchema>, data?.branchName ?? '');
      setValue(
        'moderationMandatory' as Path<TSchema>,
        data?.moderationMandatory,
      );
      setValue('dmgApp' as Path<TSchema>, data?.dmgApp ?? false);
      setValue(
        'displayInspectionVideoTutorial' as Path<TSchema>,
        data?.displayInspectionVideoTutorial ?? false,
      );
      setValue(
        'inspectionTutorialVideo' as Path<TSchema>,
        data?.inspectionTutorialVideo ?? null,
      );
    }
  }, [setValue, data]);

  return (
    <>
      <form onSubmit={onSubmit}>
        <Card
          title={
            <TitleSection
              text={t('common:settings')}
              defaultIconClassName="ki-setting-2"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled || isPendingUploadVideo}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <>
            <FormField text="" containerClassName="mb-1">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <h3 className="font-semibold text-sm">
                  {t('groupNameSpecific')}
                </h3>
              </div>
            </FormField>
            <FormField text={t('common:company')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register && register('companyName' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.companyName?.message as string)
                  }
                  placeholder={t('common:company')}
                  type="text"
                />
              </div>
            </FormField>
            <FormField text={t('common:branch')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register && register('branchName' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.branchName?.message as string)
                  }
                  placeholder={t('common:branch')}
                  type="text"
                />
              </div>
            </FormField>
            <div className="flex flex-wrap gap-5 items-center max-sm:gap-0">
              <FormField
                text={t('common:moderationMandatory')}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                  <Controller
                    name="moderationMandatory"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        leftTextClassName="self-start"
                        errorMessage={
                          errors &&
                          (errors.moderationMandatory?.message as string)
                        }
                        checked={field.value}
                        onChange={(checked) => {
                          field.onChange(checked);
                        }}
                      />
                    )}
                  />
                </div>
              </FormField>
              <TextInfo
                containerClassName="mt-3"
                text={`${t('validatedModerationWillBeOverriden')}.`}
              />
            </div>
            <FormField text={t('dmgDetectionApp')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                <Controller
                  name="dmgApp"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      leftTextClassName="self-start"
                      errorMessage={
                        errors &&
                        (errors.moderationMandatory?.message as string)
                      }
                      checked={field.value}
                      onChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                  )}
                />
              </div>
            </FormField>
            <FormField
              text={t('displayInspectionVideoTutorial')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                <Controller
                  name="displayInspectionVideoTutorial"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      leftTextClassName="self-start"
                      errorMessage={
                        errors &&
                        (errors.displayInspectionVideoTutorial
                          ?.message as string)
                      }
                      checked={field.value}
                      onChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                  )}
                />
              </div>
            </FormField>
            {/* Hidden instead of unmounted, so VideoUpload keeps the newly uploaded video when toggled */}
            <FormField
              containerClassName={
                displayInspectionVideoTutorialValue ? '' : 'hidden'
              }
              text={t('inspectionTutorialVideo')}
              subText={
                <div>
                  {t('common:allowedFormat')} :{' '}
                  {ALLOWED_VIDEO_FORMATS.join(', ').toUpperCase()}
                </div>
              }
              textWrapperClassName="mt-6"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-6">
                <VideoUpload
                  t={t}
                  initialVideo={
                    data?.inspectionTutorialVideo &&
                    `${R2_PUBLIC_DOMAIN}/${data?.inspectionTutorialVideo}`
                  }
                  allowedFormats={ALLOWED_VIDEO_FORMATS}
                  disabledChooseAnotherVideoButton={isPendingUploadVideo}
                  mutateUploadVideo={uploadVideo}
                />
              </div>
            </FormField>
          </>
        </Card>
      </form>
      {isPendingUploadVideo && <Loader />}
    </>
  );
};
